/**
 * ConfirmModal — DEPRECATED ADAPTER over the design system's `ConfirmDialog`.
 *
 * This file used to make six system-wide decisions from inside app code: button
 * order, which control takes initial focus, whether a danger dialog closes on
 * backdrop click, where "this cannot be undone" lives, how a busy action is shown,
 * and when a typed confirmation phrase is warranted. All six now belong to
 * `ConfirmDialog` (jrni-ui, 2026-08-19), so what remains here is a prop mapping
 * for the app's existing call sites — nothing more.
 *
 * Expect dialogs to CHANGE, not merely be re-imported: confirm now sits on the
 * right, cancel takes initial focus on a danger dialog, and a danger dialog no
 * longer closes on a backdrop click (Escape and Cancel still do).
 *
 * NEW CODE SHOULD IMPORT `ConfirmDialog` DIRECTLY. Two of its capabilities are
 * unreachable through this shim by design: `consequence` (the tinted "this cannot
 * be undone" surface, which must not be body copy) and `confirmPhrase` (type-to-
 * confirm, for the irreversible high-blast-radius case only).
 */
import type { ReactNode } from "react";
import { ConfirmDialog } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";

export interface ConfirmModalProps {
  open: boolean;
  title: ReactNode;
  /** Body copy (string or nodes). */
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
  /** Disables both buttons + blocks backdrop/escape close while an action runs. */
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export default function ConfirmModal({
  open, title, message, confirmLabel, cancelLabel, danger, busy, onConfirm, onClose,
}: ConfirmModalProps) {
  const t = useT();
  return (
    <ConfirmDialog
      open={open}
      onCancel={onClose}
      onConfirm={onConfirm}
      title={title}
      body={message}
      tone={danger ? "danger" : "neutral"}
      busy={busy}
      confirmLabel={confirmLabel ?? t("shell.confirmModal.confirm", "Confirm")}
      cancelLabel={cancelLabel ?? t("shell.confirmModal.cancel", "Cancel")}
      maxWidth={440}
    />
  );
}
